import React from 'react';
import { Controller } from 'react-hook-form';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption } from '@fluentui/react';
import {
  Field,
  Radio,
  RadioGroup,
  FluentProvider,
  webLightTheme,
  Textarea,
  Input,
} from '@fluentui/react-components';
import { useQuery } from '@apollo/client';
import IFinancialStatementFilingInfoFormProps from './IFinancialStatementFilingInfoFormProps';
import IFieldsetProps from '../../../../utils/types/IFieldsetProps';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import GET_FINANCIAL_STATEMENT_TYPE from '../../../../utils/statApi/CompanyFinancialStatementTypesApi';
import { yesOption, noOption } from '../../../../utils/types/CommonDropdownOptions';

const FinancialStatementFilingInfoForm = ({
  annualFilingRequirements,
  form,
}: IFinancialStatementFilingInfoFormProps): JSX.Element => {
  const { control, errors, setValue } = form;

  const FinancialStatementTypeDropdown = (props: IFieldsetProps): JSX.Element => {
    const { value, label, required, errors: accErrors, handleChange, valueKey, outputKey } = props;
    const { loading, data } = useQuery(GET_FINANCIAL_STATEMENT_TYPE);
    const { financialStatementTypes } = !loading && data ? data : [];

    return (
      <QueryBasedDropdown
        value={value as IDropdownReference}
        outputKey={outputKey}
        label={label}
        required={required}
        errors={accErrors}
        dropdownOptionsData={financialStatementTypes}
        handleChange={handleChange}
        valueKey={valueKey}
      />
    );
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <Controller
          render={(props) => (
            <FinancialStatementTypeDropdown
              label="Financial Statement Type"
              valueKey="financialStatementType"
              value={props.value}
              handleChange={(newValue: IDropdownOption, valueKey: string): void => {
                setValue('financialStatementType', newValue);
              }}
              errors={errors}
            />
          )}
          name="financialStatementType"
          control={control}
          defaultValue={annualFilingRequirements.financialStatementType}
          value={annualFilingRequirements.financialStatementType}
        />
        <Controller
          name="fsSigner"
          control={control}
          defaultValue={annualFilingRequirements.fsSigner || ''}
          rules={{
            maxLength: {
              value: 250,
              message: 'Input is greater than the maximum character limit of 250.',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }): React.ReactElement => (
            <Field label="FS Signer" validationMessage={errors?.fsSigner?.message as string}>
              <Textarea
                id="fsSigner"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="otherFinancialStatementInfo"
          control={control}
          defaultValue={annualFilingRequirements.otherFinancialStatementInfo || ''}
          rules={{
            maxLength: {
              value: 1000,
              message: 'Input is greater than the maximum character limit of 1000.',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }): React.ReactElement => (
            <Field
              label="Other Financial Statement Info"
              validationMessage={errors?.otherFinancialStatementInfo?.message as string}
            >
              <Textarea
                id="otherFinancialStatementInfo"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="taxId"
          control={control}
          defaultValue={annualFilingRequirements?.taxId || ''}
          rules={{
            maxLength: {
              value: 75,
              message: 'Input is greater than the maximum character limit of 75',
            },
            required: {
              value: true,
              message: 'Please enter a Tax ID',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }): React.ReactElement => (
            <Field label="Tax ID" required validationMessage={errors?.taxId?.message as string}>
              <Input
                id="taxId"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
              />
            </Field>
          )}
        />
        <Controller
          name="physicalSignatureRequired"
          control={control}
          defaultValue={annualFilingRequirements.physicalSignatureRequired}
          render={({ value }): React.ReactElement => (
            <Field label="Physical Signature Required">
              <RadioGroup
                id="physicalSignatureRequired"
                name="physicalSignatureRequired"
                value={value?.toString()}
                onChange={(e, radioData): void => {
                  setValue('physicalSignatureRequired', radioData.value === 'true');
                }}
              >
                <Radio value={yesOption.key} label={yesOption.text} />
                <Radio value={noOption.key} label={noOption.text} />
              </RadioGroup>
            </Field>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default FinancialStatementFilingInfoForm;
